import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { SaveIndicator, StatusLabel } from "@/studio/ui";
import { projectPayload, roomToForm } from "@/studio/rooms";
import { StepBudget, StepPriorities, StepRooms, StepStyle } from "@/studio/intake/StageOne";
import { StepFinish, StepFurniture, StepMeasure, areaOf } from "@/studio/intake/StageTwo";
import { SummaryView } from "@/studio/intake/ReviewStep";
import { StageTag } from "@/studio/ProjectPanels";

const StepReview = ({ ctx, missing, jump, readOnly }) => (
  <div>
    <p className="st-ask">{readOnly ? "Saved answers" : "A quick look before completing."}</p>
    <SummaryView b={ctx.view} sections={ctx.sections} onEdit={readOnly ? null : jump} />
    {!readOnly && missing.length > 0 && (
      <div className="st-sum" style={{ marginTop: 14 }} data-testid="review-pending">
        <div className="st-sum__head"><h3>Pending items</h3></div>
        {missing.map((m) => <div className="st-sum__line" key={m.text}><span>{m.text}</span><button className="st-btn st-btn--quiet st-btn--sm" onClick={() => jump(m.step)}>Go</button></div>)}
        <p className="st-muted st-small">You can still complete; pending items stay on the project.</p>
      </div>
    )}
  </div>
);

const STEPS = [
  ["Rooms & Needs", StepRooms], ["Budget & Timeline", StepBudget], ["Style & Color", StepStyle], ["Priorities & Deal Breakers", StepPriorities],
  ["Measurements & Photos", StepMeasure], ["Furniture & Requirements", StepFurniture], ["Optional Services & Finish", StepFinish], ["Review & Complete", StepReview],
];
const VISIBLE = { stage1: [0, 1, 2, 3, 7], stage2: [4, 5, 6, 7], combined: [0, 1, 2, 3, 4, 5, 6, 7] };
const SECTIONS = { stage1: ["discovery"], stage2: ["assessment"], combined: ["discovery", "assessment"] };
const TITLE = { stage1: "Stage 1 — Discovery Call", stage2: "Stage 2 — In-Home Assessment", combined: "Combined Consultation — Discovery + Assessment" };
const PEND = { dimensions: "dimensions", openings: "door/window measurements", photos: "photos", floorplan: "floor plan" };

const missingItems = (vis, pa, rooms, measures, files, ra, sel) => {
  const out = [];
  (pa.skipped || []).filter((s) => vis.includes(s)).forEach((s) => out.push({ step: s, text: `Skipped: ${STEPS[s][0]}` }));
  if (vis.includes(1) && !pa.timeline) out.push({ step: 1, text: "Design timeline not chosen" });
  if (vis.includes(2) && !sel.styles.length) out.push({ step: 2, text: "No styles selected" });
  if (vis.includes(4)) rooms.forEach((r) => {
    const m = measures[r.id] || {};
    (m.pending || []).forEach((p) => out.push({ step: 4, text: `${r.label}: ${PEND[p]} pending` }));
    if (!(m.confirmed && areaOf(m)) && r.size_status !== "confirmed") out.push({ step: 4, text: `${r.label}: measurements not confirmed` });
    if (!files.some((f) => f.room_id === r.id && f.kind === "photo") && !(m.pending || []).includes("photos")) out.push({ step: 4, text: `${r.label}: no photos yet` });
    if (!Object.keys(ra(r.id).furniture || {}).length) out.push({ step: 5, text: `${r.label}: furniture checklist empty` });
  });
  return out.map((m) => ({ ...m, title: STEPS[m.step][0] }));
};

const pickStage = (q, project) => {
  if (q) return q;
  if (project.consultation_type === "remote") return "combined";
  return project.workflow.stages[0].status === "completed" ? "stage2" : "stage1";
};

export default function Consultation() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [selections, setSel] = useState(null);
  const [measures, setMeasures] = useState({});
  const [rooms, setRoomsState] = useState([]);
  const [files, setFiles] = useState([]);
  const [step, setStepState] = useState(0);
  const [save, setSave] = useState("idle");
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const dirty = useRef({});
  const chain = useRef(Promise.resolve());
  const latest = useRef({});
  const stage = b ? pickStage(params.get("stage"), b.project) : null;
  const info = b?.project.workflow.stages.find((s) => s.stage === stage);
  const completed = info?.status === "completed";
  const mode = completed ? (params.get("mode") === "edit" ? "edit" : "view") : "draft";
  latest.current = { answers, selections, measures, rooms, step, project: b?.project, stage, mode };

  const hydrate = (data) => {
    setB(data);
    setAnswers({ project: data.answers.project || {}, rooms: data.answers.rooms || {} });
    setSel({ styles: data.selections.styles.map((s) => s.id), palettes: data.selections.palettes.map((p) => p.id),
      rooms: Object.fromEntries(Object.entries(data.selections.rooms || {}).map(([k, v]) => [k, { styles: v.styles.map((s) => s.id), palettes: v.palettes.map((p) => p.id) }])) });
    setMeasures(data.measurements);
    setRoomsState(data.project.rooms.map(roomToForm));
    setFiles(data.files);
  };
  const load = useCallback(async () => {
    const { data } = await adminApi.get(`/studio/consultations/${id}`);
    hydrate(data);
    const st = pickStage(params.get("stage"), data.project);
    const inf = data.project.workflow.stages.find((s) => s.stage === st);
    const vis = VISIBLE[st];
    const done = inf?.status === "completed";
    setStepState(done ? (params.get("mode") === "edit" ? vis[0] : 7) : vis.includes(inf?.step) ? inf.step : vis[0]);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const flush = (edit = false) => {
    chain.current = chain.current.then(async () => {
      const d = dirty.current;
      dirty.current = {};
      const L = latest.current;
      if (!Object.keys(d).length) return;
      setSave("saving");
      if (d.rooms) {
        const { data } = await adminApi.put(`/studio/projects/${L.project.id}`, projectPayload({ ...L.project, rooms: L.rooms }));
        setB((x) => ({ ...x, project: data }));
      }
      const body = { stage: L.stage, edit };
      if (d.step && L.mode === "draft") body.step = L.step;
      if (d.answers) body.answers = L.answers;
      if (d.selections) body.selections = L.selections;
      if (d.measures) body.measurements = L.measures;
      if (Object.keys(body).length > 2 || (edit && d.rooms)) {
        const { data } = await adminApi.put(`/studio/consultations/${id}`, body);
        setB(data);
        if (d.measures) setRoomsState((rs) => rs.map((r) => { const s = data.project.rooms.find((x) => x.id === r.id); return s ? { ...r, size_sqft: s.size_sqft ?? "", size_status: s.size_status } : r; }));
      }
      setSave("saved");
    }).catch((err) => { setSave("error"); toast.error(formatApiError(err.response?.data?.detail, "Not saved")); throw err; });
    return chain.current;
  };
  useEffect(() => {
    if (!tick || latest.current.mode !== "draft") return undefined;
    setSave("pending");
    const t = setTimeout(() => flush().catch(() => {}), 600);
    return () => clearTimeout(t);
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const flushNow = () => {
    const d = dirty.current;
    const L = latest.current;
    if (L.mode !== "draft" || !L.project || !Object.keys(d).length) return;
    dirty.current = {};
    const send = (path, body) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, {
      method: "PUT", keepalive: true, body: JSON.stringify(body),
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("hausa_admin_token")}` },
    }).catch(() => {});
    if (d.rooms) send(`/studio/projects/${L.project.id}`, projectPayload({ ...L.project, rooms: L.rooms }));
    const body = { stage: L.stage, edit: false };
    if (d.step) body.step = L.step;
    if (d.answers) body.answers = L.answers;
    if (d.selections) body.selections = L.selections;
    if (d.measures) body.measurements = L.measures;
    if (Object.keys(body).length > 2) send(`/studio/consultations/${id}`, body);
  };
  useEffect(() => {
    const hidden = () => { if (document.visibilityState === "hidden") flushNow(); };
    const blur = () => { if (latest.current.mode === "draft" && Object.keys(dirty.current).length) flush().catch(() => {}); };
    document.addEventListener("visibilitychange", hidden);
    window.addEventListener("pagehide", flushNow);
    window.addEventListener("beforeunload", flushNow);
    document.addEventListener("focusout", blur);
    return () => {
      document.removeEventListener("visibilitychange", hidden);
      window.removeEventListener("pagehide", flushNow);
      window.removeEventListener("beforeunload", flushNow);
      document.removeEventListener("focusout", blur);
      flushNow();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.querySelector(".st-step.is-on")?.scrollIntoView({ inline: "center", block: "nearest" }); }, [step, b]);

  if (!b || !answers) return <p className="st-loading">Loading…</p>;
  const vis = VISIBLE[stage];
  const pa = answers.project;
  const mark = (part) => { dirty.current[part] = true; if (mode === "edit") setSave("pending"); setTick((t) => t + 1); };
  const setPa = (patch) => { setAnswers((a) => ({ ...a, project: { ...a.project, ...patch } })); mark("answers"); };
  const ra = (rid) => answers.rooms[rid] || {};
  const setRa = (rid, patch) => { setAnswers((a) => ({ ...a, rooms: { ...a.rooms, [rid]: { ...(a.rooms[rid] || {}), ...patch } } })); mark("answers"); };
  const setRooms = (fn) => { setRoomsState(fn); mark("rooms"); };
  const measure = (rid) => measures[rid] || {};
  const setMeasure = (rid, patch) => { setMeasures((m) => ({ ...m, [rid]: { ...(m[rid] || {}), ...patch } })); mark("measures"); };
  const setSelections = (s) => { setSel(s); mark("selections"); };
  const go = (s) => { setStepState(s); if (mode === "draft") { dirty.current.step = true; latest.current = { ...latest.current, step: s }; flush().catch(() => {}); } window.scrollTo(0, 0); };
  const pos = vis.indexOf(step);
  const next = vis[pos + 1];
  const prev = vis[pos - 1];
  const skip = () => { setPa({ skipped: [...new Set([...(pa.skipped || []), step])] }); go(next); };
  const forward = () => { if ((pa.skipped || []).includes(step)) setPa({ skipped: pa.skipped.filter((s) => s !== step) }); go(next); };
  const back = () => navigate(`/admin/studio/projects/${b.project.id}`);

  const complete = async () => {
    setBusy(true);
    try {
      await flush();
      await adminApi.post(`/studio/consultations/${id}/complete`, { stage });
      toast.success(`${TITLE[stage]} completed`);
      back();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const saveEdits = async () => {
    setBusy(true);
    try { await flush(true); toast.success("Changes saved"); setParams({ stage, mode: "view" }); setStepState(7); } catch { /* toast shown */ } finally { setBusy(false); }
  };
  const cancelEdits = async () => { dirty.current = {}; await load(); setParams({ stage, mode: "view" }); setSave("idle"); toast("Changes discarded"); };
  const startEdit = () => { setParams({ stage, mode: "edit" }); setStepState(vis[0]); };

  const libById = Object.fromEntries([...b.library.styles, ...b.library.palettes].map((x) => [x.id, x]));
  const ids = (arr) => arr.map((i) => libById[i]).filter(Boolean);
  const view = { project: { ...b.project, rooms: b.project.rooms.map((r) => ({ ...r, ...(rooms.find((x) => x.id === r.id) ? { purpose: rooms.find((x) => x.id === r.id).purpose } : {}) })) },
    answers, measurements: measures, files,
    selections: { styles: ids(selections.styles), palettes: ids(selections.palettes), rooms: Object.fromEntries(Object.entries(selections.rooms || {}).map(([k, v]) => [k, { styles: ids(v.styles), palettes: ids(v.palettes) }])) } };
  const ctx = {
    rooms, setRooms, ra, setRa, pa, setPa, measure, setMeasure, files, setFiles, selections, setSelections, view, sections: SECTIONS[stage],
    library: b.library, meta: { room_types: [["primary_bedroom", "Primary bedroom"], ["secondary_bedroom", "Secondary bedroom"], ["living_room", "Living room"], ["dining_room", "Dining room"], ["living_dining_flex", "Living / dining flex"], ["patio", "Patio"], ["other", "Other"]].map(([value, label]) => ({ value, label })) },
    serverRooms: Object.fromEntries(b.project.rooms.map((r) => [r.id, r])), previews: b.project.furniture_budgets, projectId: b.project.id,
  };
  const readOnly = mode === "view";
  const Step = STEPS[readOnly ? 7 : step][1];
  const missing = step >= 6 ? missingItems(vis, pa, rooms, measures, files, ra, selections) : [];

  return (
    <div className="st-page st-page--intake" data-testid="consultation-page">
      <header className="st-head" style={{ marginBottom: 22 }}>
        <div>
          <p className="st-eyebrow"><Link to={`/admin/studio/projects/${b.project.id}`} data-testid="consultation-project-link">{b.project.name}</Link> · {b.project.client?.full_name}</p>
          <h1 className="st-h1" style={{ fontSize: "clamp(1.6rem, 3vw, 2.5rem)" }} data-testid="studio-page-title">{TITLE[stage]}</h1>
          <p className="st-lead" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <StageTag st={info} testId="consultation-status" />
            <StatusLabel status={b.project.status} testId="consultation-project-status" />
          </p>
        </div>
        {mode !== "view" && <SaveIndicator state={mode === "edit" && save === "pending" ? "pending" : save} />}
      </header>
      {mode === "edit" && (
        <div className="st-editbar" data-testid="edit-mode-bar">
          <span>Editing a completed consultation. Changes are kept only when you save.</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button className="st-btn st-btn--ghost st-btn--sm" onClick={cancelEdits} data-testid="edit-cancel-button">Cancel</button>
            <button className="st-btn st-btn--sm" disabled={busy} onClick={saveEdits} data-testid="edit-save-button">Save changes</button>
          </span>
        </div>
      )}
      {readOnly ? (
        <div style={{ maxWidth: 920 }}>
          <StepReview ctx={ctx} missing={[]} jump={go} readOnly />
          <div className="st-intake-foot">
            <button className="st-btn st-btn--ghost" onClick={back} data-testid="view-back-button">Back to project</button>
            <button className="st-btn" onClick={startEdit} data-testid="view-edit-button">Edit</button>
          </div>
        </div>
      ) : (
        <>
          <nav className="st-steps" data-testid="consultation-steps">
            {vis.map((s, i) => (
              <button key={s} className={`st-step ${s === step ? "is-on" : ""} ${i < pos ? "is-done" : ""}`} onClick={() => go(s)} data-testid={`consultation-step-${s}`}>
                <b>{i + 1}/{vis.length}</b><span>{STEPS[s][0]}</span>
              </button>
            ))}
          </nav>
          <div style={{ maxWidth: 920 }} key={step}>
            <Step ctx={ctx} missing={missing} jump={go} />
            <div className="st-intake-foot">
              <div style={{ display: "flex", gap: 8 }}>
                {prev !== undefined && <button className="st-btn st-btn--ghost" onClick={() => go(prev)} data-testid="consultation-back-button">Back</button>}
                {next !== undefined && next !== 7 && mode === "draft" && <button className="st-btn st-btn--quiet" onClick={skip} data-testid="consultation-skip-button">Skip</button>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {step === 7 && mode === "draft" && <button className="st-btn" disabled={busy} onClick={complete} data-testid="complete-stage-button">Complete</button>}
                {step === 7 && mode === "edit" && <button className="st-btn" disabled={busy} onClick={saveEdits} data-testid="edit-save-bottom">Save changes</button>}
                {next !== undefined && <button className="st-btn" onClick={forward} data-testid="consultation-next-button">{next === 7 ? "Review" : "Next"}</button>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
